import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  DEFAULT_REVIEW_MODE_STATE,
  parseReviewSinceDate,
  readReviewModeState,
  writeReviewModeState,
} from '../../utils/stockReviewMode';

const StockReviewModeContext = createContext(null);

function TickerReviewModeProvider({ ticker, children }) {
  const [reviewMode, setReviewMode] = useState(() => readReviewModeState(ticker));

  useEffect(() => {
    writeReviewModeState(ticker, reviewMode);
  }, [reviewMode, ticker]);

  const setSinceDate = useCallback((value) => {
    const sinceDate = parseReviewSinceDate(value);
    setReviewMode((current) => ({ ...current, sinceDate }));
  }, []);

  const setEnabled = useCallback((enabled) => {
    setReviewMode((current) => ({ ...current, enabled: enabled === true }));
  }, []);

  const clear = useCallback(() => {
    setReviewMode({ ...DEFAULT_REVIEW_MODE_STATE });
  }, []);

  const value = useMemo(() => ({
    enabled: reviewMode.enabled,
    sinceDate: reviewMode.sinceDate,
    setSinceDate,
    setEnabled,
    clear,
    isActive: reviewMode.enabled && Boolean(reviewMode.sinceDate),
  }), [clear, reviewMode, setEnabled, setSinceDate]);

  return (
    <StockReviewModeContext.Provider value={value}>
      {children}
    </StockReviewModeContext.Provider>
  );
}

export function StockReviewModeProvider({ ticker, children }) {
  const normalizedTicker = typeof ticker === 'string'
    ? ticker.trim().toUpperCase()
    : '';

  return (
    <TickerReviewModeProvider key={normalizedTicker} ticker={normalizedTicker}>
      {children}
    </TickerReviewModeProvider>
  );
}

export function useStockReviewMode() {
  const context = useContext(StockReviewModeContext);
  if (!context) {
    throw new Error('useStockReviewMode must be used within StockReviewModeProvider');
  }
  return context;
}
